import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown, ChevronUp } from 'lucide-react';

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isProjectsOpen, setIsProjectsOpen] = useState(false);
  
  // Track scrolling to change navbar appearance
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    
    // Initialize scroll state
    handleScroll();
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const projects = [
    { title: "Cloud Automation at Syntasa", path: "/projects/cloud-automation" },
    { title: "AWS SaaS Migration", path: "/projects/aws-migration" },
    { title: "GCP Oklahoma Modernization", path: "/projects/gcp-oklahoma" },
    { title: "Data Visualization Improvements", path: "/projects/data-visualization" }
  ];
  
  // Define background styles
  const bgStyle = 'bg-white text-gray-800 shadow-md py-2';
    
  // Define hover styles
  const hoverStyle = isScrolled
    ? 'hover:text-blue-600'
    : 'hover:text-blue-200';
    
  // Define button styles
  const buttonStyle = isScrolled
    ? 'bg-blue-600 text-white'
    : 'bg-white text-blue-600';
  
  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${bgStyle}`}>
      <div className="container mx-auto px-6">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <img 
              src="/assets/logos/Logo-01.svg" 
              alt="Philip Crilley Logo" 
              className={`h-10 transition-all duration-300 ${isScrolled ? '' : 'filter brightness-0 invert'}`}
            />
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-10">
            <a href="/" className={`transition duration-300 ${hoverStyle}`}>Home</a>
            <a href="/about" className={`transition duration-300 ${hoverStyle}`}>About</a>
            
            {/* Projects Dropdown */}
            <div className="relative">
              <button 
                className={`flex items-center transition duration-300 ${hoverStyle}`}
                onClick={() => setIsProjectsOpen(!isProjectsOpen)}
              >
                Projects
                {isProjectsOpen ? (
                  <ChevronUp className="ml-1" size={16} />
                ) : (
                  <ChevronDown className="ml-1" size={16} />
                )}
              </button>
              
              {/* Projects Dropdown Menu */}
              {isProjectsOpen && (
                <div className="absolute right-0 mt-2 w-72 bg-white rounded-md shadow-lg py-2 z-10">
                  {projects.map((project, index) => (
                    <a
                      key={index}
                      href={project.path}
                      className="block px-4 py-2 text-gray-800 hover:bg-blue-50 hover:text-blue-600"
                    >
                      {project.title}
                    </a>
                  ))}
                </div>
              )}
            </div>
            
            <a href="/skills" className={`transition duration-300 ${hoverStyle}`}>Skills</a>
            <a href="/contact" className={`transition duration-300 ${hoverStyle}`}>Contact</a>
          </div>
          
          {/* Contact Button (Desktop) */}
          <div className="hidden md:block">
            <a 
              href="/contact" 
              className={`${buttonStyle} px-6 py-2 rounded-lg font-semibold transition duration-300 hover:opacity-90`}
            >
              Hire Me
            </a>
          </div>
          
          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="focus:outline-none"
            >
              {isMenuOpen ? (
                <X size={24} />
              ) : (
                <Menu size={24} />
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile Navigation Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-white text-gray-800">
          <div className="container mx-auto px-6 py-4">
            <div className="flex flex-col space-y-4">
              <a 
                href="/" 
                className="py-2 hover:text-blue-500 transition duration-300"
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </a>
              <a 
                href="/about" 
                className="py-2 hover:text-blue-500 transition duration-300"
                onClick={() => setIsMenuOpen(false)}
              >
                About
              </a>
              
              {/* Mobile Projects Dropdown */}
              <div>
                <button 
                  className="w-full flex items-center justify-between py-2 hover:text-blue-500 transition duration-300"
                  onClick={() => setIsProjectsOpen(!isProjectsOpen)}
                >
                  Projects
                  {isProjectsOpen ? (
                    <ChevronUp size={16} />
                  ) : (
                    <ChevronDown size={16} />
                  )}
                </button>
                
                {isProjectsOpen && (
                  <div className="pl-4 mt-2 space-y-2 border-l-2 border-blue-100">
                    {projects.map((project, index) => (
                      <a
                        key={index}
                        href={project.path}
                        className="block py-2 hover:text-blue-500 transition duration-300"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {project.title}
                      </a>
                    ))}
                  </div>
                )}
              </div>
              
              <a 
                href="/skills" 
                className="py-2 hover:text-blue-500 transition duration-300"
                onClick={() => setIsMenuOpen(false)}
              >
                Skills
              </a>
              <a 
                href="/contact" 
                className="py-2 hover:text-blue-500 transition duration-300"
                onClick={() => setIsMenuOpen(false)}
              >
                Contact
              </a>
              
              <div className="pt-4">
                <a 
                  href="/contact" 
                  className="block w-full text-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold transition duration-300 hover:bg-blue-700"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Hire Me
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;